import { filterMenuCategories } from "utils/menuSearch";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useFavorites } from "../contexts/FavoritesContext";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  LogOut,
  Menu,
  X,
  ChevronDown,
  Star,
  User,
  // Monitor,
  Search,
  Bell,
  // Moon,
  // Sun,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";
import { cn } from "../ui/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import logoImage from "../assets/receiptLogo.png";
import {
  canViewModule,
  hasUserManagementAccess,
  hasRoleManagementAccess,
} from "../utils/permissions";
import type { ModuleName } from "../types/permissions";
// import { UserRole } from "../contexts/UserAuthContext";
import * as Icons from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHeaderConfig, PageHeaderContext } from "../contexts/PageHeaderContext";
import React from "react";
import { getTourOperatorWithDocuments } from "../services/tourService";

// ── Notifications ─────────────────────────────────────────────────────────────
interface Notification {
  id: string;
  type: 'venue_approved' | 'venue_rejected' | 'tour_rescheduled' | 'tour_approved' | 'venue_recommend' | 'room_shortage';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

const mockNotifications: Notification[] = [
  {
    id: 'n1',
    type: 'venue_approved',
    title: 'Venue Approved',
    message: 'Tour TOUR-60 "Safar 14456H" venue allocation at Karbala Palace has been approved.',
    time: '2 min ago',
    read: false,
  },
  {
    id: 'n2',
    type: 'tour_rescheduled',
    title: 'Tour Rescheduled',
    message: 'Tour RB-2026-032 "Najaf Ziyarat June" has been rescheduled to 15 Jun – 20 Jun 2026.',
    time: '18 min ago',
    read: false,
  },
  {
    id: 'n3',
    type: 'room_shortage',
    title: 'Room Shortage Alert',
    message: 'Insufficient rooms detected for TOUR-75 "Karbala Ashura 1446H". Recommend new dates.',
    time: '1 hr ago',
    read: false,
  },
  {
    id: 'n4',
    type: 'tour_approved',
    title: 'Tour Approved',
    message: 'Tour TOUR-58 "Ziyarat Najaf May" has been fully approved and is now active.',
    time: '3 hrs ago',
    read: true,
  },
  {
    id: 'n5',
    type: 'venue_rejected',
    title: 'Venue Rejected',
    message: 'Venue allocation for tour IB-2026-009 was rejected. New dates have been recommended.',
    time: '5 hrs ago',
    read: true,
  },
  {
    id: 'n6',
    type: 'venue_recommend',
    title: 'New Dates Recommended',
    message: 'Admin has recommended new dates for TOUR-62 due to unavailability at Najaf Grand Hotel.',
    time: 'Yesterday',
    read: true,
  },
  {
    id: 'n7',
    type: 'venue_approved',
    title: 'Venue Approved',
    message: 'Tour TOUR-55 "Muharram Pilgrimage" venue at Al-Abbas Hotel, Karbala has been approved.',
    time: 'Yesterday',
    read: true,
  },
];

const notificationIcon = (type: Notification['type']) => {
  const base = 'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0';
  switch (type) {
    case 'venue_approved': return <div className={cn(base, 'bg-green-100')}><CheckCircleIcon className="w-4 h-4 text-green-600" /></div>;
    case 'venue_rejected': return <div className={cn(base, 'bg-red-100')}><XCircleIcon className="w-4 h-4 text-red-600" /></div>;
    case 'tour_rescheduled': return <div className={cn(base, 'bg-amber-100')}><CalendarClockIcon className="w-4 h-4 text-amber-600" /></div>;
    case 'tour_approved': return <div className={cn(base, 'bg-blue-100')}><CheckCircleIcon className="w-4 h-4 text-blue-600" /></div>;
    case 'venue_recommend': return <div className={cn(base, 'bg-purple-100')}><CalendarClockIcon className="w-4 h-4 text-purple-600" /></div>;
    case 'room_shortage': return <div className={cn(base, 'bg-orange-100')}><AlertTriangleIcon className="w-4 h-4 text-orange-600" /></div>;
    default: return <div className={cn(base, 'bg-gray-100')}><BellIcon className="w-4 h-4 text-gray-500" /></div>;
  }
};

// Mini icon shims — use inline SVG to avoid extra imports
const CheckCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const XCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const CalendarClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);
const AlertTriangleIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);
const BellIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

interface DashboardLayoutProps {
  children: ReactNode;
  currentPage: string;
  onNavigate: (page: string) => void;
}


interface MenuItem {
  label: string;
  icon: ReactNode;
  iconColor: string;
  iconBg: string;
  path: string;
  moduleName: ModuleName;
  children?: MenuItem[]; // ✅ for nested UI
  screenName?: string;
  items?: MenuItem[]
}

interface MenuCategory {
  id: string;
  label: string;
  color: string;
  items: MenuItem[];
}

interface ApiMenuItem {
  moduleScreenId: number;
  screenCode: string;
  screenName: string;
  label: string;
  icon: string;
  iconColor: string;
  iconBg: string;
  path: string | null;
  moduleName: string;
  items?: ApiMenuItem[]; // ✅ children support
}

export interface ApiMenuCategory {
  moduleId: number;
  id: string;
  moduleName: string;
  moduleCode: string;
  label: string;
  color: string;
  items: ApiMenuItem[];
}



export function DashboardLayoutNew({
  children,
  currentPage,
  onNavigate,
}: DashboardLayoutProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { user, menuCategoriesList, logout } = useAuth();
  // const { toggleFavorite, isFavorite } = useFavorites();
  const { isFavorite } = useFavorites();
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const { sidebarCollapsed, toggleSidebar } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [searchExpandedCategories, setSearchExpandedCategories] = useState<{
    query: string;
    ids: string[];
  } | null>(null);
  const [searchOpenItems, setSearchOpenItems] = useState<{
    query: string;
    items: Record<string, boolean>;
  } | null>(null);
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const isSearching = normalizedSearch.length > 0;
  const navigate = useNavigate();
  const [pageHeader, setPageHeader] = useState<PageHeaderConfig | null>(null);

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifTab, setNotifTab] = useState<'all' | 'unread'>('all');
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const notifPanelRef = useRef<HTMLDivElement>(null);

  const [operatorExpiryDate, setOperatorExpiryDate] = useState<Date | null>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notificationsOpen && notifPanelRef.current && !notifPanelRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [notificationsOpen]);

  useEffect(() => {
    const fetchOperatorExpiry = async () => {
      try {
        const tourOperatorId = user?.tourOperatorId;

        if (!tourOperatorId) return;

        const res = await getTourOperatorWithDocuments(tourOperatorId);

        const expiry = res.partners?.find(
          (p) => p.isMainOperator
        )?.permissionExpireDate;

        if (expiry) {
          const [datePart] = expiry.split(" ");

          const [mm, dd, yyyy] = datePart.split("/").map(Number);

          const parsed = new Date(yyyy, mm - 1, dd);

          setOperatorExpiryDate(parsed);
        } else {
          setOperatorExpiryDate(null);
        }
      } catch (error) {
        console.error("Failed to fetch operator expiry date", error);
        setOperatorExpiryDate(null);
      }
    };

    fetchOperatorExpiry();
  }, [user?.tourOperatorId]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  const markRead = (id: string) => setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));

  const displayedNotifications = notifTab === 'unread' ? notifications.filter(n => !n.read) : notifications


  // const defaultIcon = <Icons.Circle className="w-4 h-4" />;
  const defaultIcon = <Icons.FolderX className="w-4 h-4" />;

  const mapMenuItems = (items: ApiMenuItem[]): MenuItem[] => {
    const iconsMap = Icons as unknown as Record<string, LucideIcon>;

    return items.map((item) => {
      const IconComponent = iconsMap[item.icon];

      const hasChildren = item.items && item.items.length > 0;

      return {
        label: item.label,
        // ✅ condition for folder icon
        icon: hasChildren ? (
          <Icons.FolderOpen className="w-4 h-4" />
        ) : IconComponent ? (
          <IconComponent className="w-4 h-4" />
        ) : (
          defaultIcon
        ),
        iconColor: item.iconColor,
        iconBg: item.iconBg,
        path: item.path || "",
        moduleName: item.moduleName as ModuleName,
        children: item.items?.length ? mapMenuItems(item.items ?? []) : [],
      };
    });
  };


  const mapApiMenuToFrontendMenu = (
    apiMenu: ApiMenuCategory[]
  ): MenuCategory[] => {
    return apiMenu.map((cat) => ({
      id: cat.id,
      label: cat.label,
      color: cat.color,
      items: mapMenuItems(cat.items),
    }));
  };


  const dynamicMenuCategories: MenuCategory[] = menuCategoriesList?.length
    ? mapApiMenuToFrontendMenu(menuCategoriesList)
    : [];


  // Filter categories and items based on user permissions and search query
  const filteredCategories = filterMenuCategories(dynamicMenuCategories, normalizedSearch);

  // Get all favorite items (filtered by search if applicable)
  const userPermissions = user?.permissions ?? {};

  const allItems = dynamicMenuCategories
    .flatMap((category) => category.items)
    .filter((item) => {
      let hasPermission = false;

      if (item.moduleName === "user-management") {
        hasPermission = hasUserManagementAccess(userPermissions);
      } else if (item.moduleName === "role-management") {
        hasPermission = hasRoleManagementAccess(userPermissions);
      } else {
        hasPermission = canViewModule(userPermissions, item.moduleName);
      }

      return hasPermission && isFavorite(item.path);
    });


  const favoriteItems = searchQuery.trim()
    ? allItems.filter((item) => {
      const query = normalizedSearch;
      return (
        item.label.toLowerCase().includes(query) ||
        item.moduleName.toLowerCase().includes(query)
      );
    })
    : allItems;

  const handleLogout = () => {
    logout();
  };

  const handleNavigation = (path: string) => {
    onNavigate(path);
    setSidebarOpen(false);
  };

  // const toggleDarkMode = () => {
  //   setDarkMode(!darkMode);
  //   document.documentElement.classList.toggle("dark");
  // };


  const isItemExpanded = (label: string) => isSearching
    ? searchOpenItems?.query === normalizedSearch
      ? searchOpenItems.items[label] ?? true
      : true
    : !!openItems[label];
  const renderMenuItems = (items: MenuItem[], level = 0) => {
    return items.map((item) => (
      <div key={item.label}>
        {/* Menu Item */}
        <button
          onClick={() => {
            if (item.children && item.children.length > 0) {
              if (isSearching) {
                setSearchOpenItems((prev) => ({
                  query: normalizedSearch,
                  items: {
                    ...(prev?.query === normalizedSearch ? prev.items : {}),
                    [item.label]: !isItemExpanded(item.label),
                  },
                }));
              } else {
                setOpenItems((prev) => ({
                  ...prev,
                  [item.label]: !prev[item.label],
                }));
              }
            } else if (item.path) {
              handleNavigation(item.path);
            }
          }}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-all",
            currentPage === item.path
              ? "bg-sky-50 text-sky-900"
              : "hover:bg-gray-50 text-gray-700"
          )}
          style={{ paddingLeft: `${12 + level * 16}px` }}
        >
          <div className={cn("p-1.5 rounded-lg", item.iconBg)}>
            {item.icon}
          </div>

          <span className="text-sm flex-1">{item.label}</span>
          {currentPage === item.path && (
            <div className="w-1.5 h-1.5 rounded-full bg-sky-500" />
          )}

          {/* Show arrow if children exist */}
          {item.children && item.children.length > 0 && (
            <ChevronDown
              className={cn(
                "w-4 h-4 transition-transform duration-200",
                isItemExpanded(item.label) ? "rotate-180" : "rotate-0"
              )}
            />
          )}
        </button>

        {/* 🔥 Recursive children */}
        {item.children &&
          item.children.length > 0 &&
          isItemExpanded(item.label) && (
            <div>
              {renderMenuItems(item.children, level + 1)}
            </div>
          )}
      </div>
    ));
  };

  const isTourOperatorExpired =
    operatorExpiryDate !== null &&
    new Date().setHours(0, 0, 0, 0) >
      new Date(operatorExpiryDate).setHours(0, 0, 0, 0);

  return (
    <PageHeaderContext.Provider value={{ pageHeader, setPageHeader }}>
      <div className="min-h-screen bg-background pattern-bg flex">

        {/* SIDEBAR */}
        <aside
          className={cn(`fixed top-0 left-0 z-40 h-screen bg-white
            border-r border-gray-200
            overflow-y-auto
            overflow-x-hidden
            transition-all duration-300`,
            //// Width based on collapse state and screen size
            sidebarCollapsed && !sidebarOpen
              ? "lg:w-16"
              : "w-64",
            //// Mobile: slide in/out
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          )}
        >

          {/* Logo Section */}
          <div
            className={cn(
              "p-2 border-b border-gray-200 flex items-center",
              sidebarCollapsed && !sidebarOpen
                ? "justify-center"
                : "gap-3 justify-between"
            )}
          >
            <div className="flex justify-center items-center gap-3 cursor-pointer" onClick={() => navigate("/dashboard")}>
              {sidebarCollapsed && !sidebarOpen ? (
                <img src={logoImage} alt="FHIMS Logo" className="w-10 h-10 hidden" />
              ) : (
                <>
                  <img src={logoImage} alt="FHIMS Logo" className="w-10 h-10" />
                  <div>
                    <h1 className="text-sky-900 text-lg">FHIMS</h1>
                    <p className="text-xs text-gray-500">Faiz E Husaini - Iraq</p>
                  </div>
                </>
              )}
            </div>


            {/* Close button for mobile */}
            {sidebarOpen && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden"
              >
                <X className="w-5 h-5" />
              </Button>
            )}

            <Button
              variant="ghost"
              size="lg"
              onClick={toggleSidebar}
              className="mb-1 hidden lg:flex shrink-0"
            >
              {sidebarCollapsed ? (
                <PanelLeft className="w-5 h-5" />
              ) : (
                <PanelLeftClose className="w-5 h-5" />
              )}
            </Button>
          </div>

          {/* Search Bar */}
          {(!sidebarCollapsed || sidebarOpen) && (
            <div className="px-3 pt-3 pb-2 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search menu..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-8 py-2 text-sm border-gray-300 focus:border-sky-400 focus:ring-sky-400"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                    title="Clear search"
                  >
                    <X className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                )}
              </div>
              {searchQuery && (
                <div className="mt-2 text-xs text-gray-600 px-1">
                  {filteredCategories.reduce(
                    (acc, cat) => acc + cat.items.length,
                    0
                  )}{" "}
                  result(s) found
                </div>
              )}
            </div>
          )}

          <div className="py-3 px-3 space-y-1">
            {/* Favorites Section */}
            {favoriteItems.length > 0 && (!sidebarCollapsed || sidebarOpen) && (
              <div className="pt-4 pb-2">
                <div className="px-3 mb-2">
                  <div className="flex items-center gap-2">
                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                    <span className="text-xs text-gray-500 uppercase tracking-wider">
                      Favorites
                    </span>
                  </div>
                </div>
                {favoriteItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-all mb-1",
                      currentPage === item.path
                        ? "bg-sky-50 text-sky-900"
                        : "hover:bg-gray-50 text-gray-700"
                    )}
                  >
                    <div className={cn("p-1.5 rounded-lg", item.iconBg)}>
                      {item.icon}
                    </div>
                    <span className="text-sm">{item.label}</span>
                    {currentPage === item.path && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full bg-sky-500" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Menu Categories */}
            {!sidebarCollapsed || sidebarOpen ? (
              filteredCategories.length > 0 ? (
                <Accordion
                  type="multiple"
                  value={isSearching
                    ? searchExpandedCategories?.query === normalizedSearch
                      ? searchExpandedCategories.ids
                      : filteredCategories.map((category) => category.id)
                    : expandedCategories}
                  onValueChange={(ids) => {
                    if (isSearching) {
                      setSearchExpandedCategories({ query: normalizedSearch, ids });
                    } else {
                      setExpandedCategories(ids);
                      setSearchExpandedCategories(null);
                    }
                  }}
                >
                  {filteredCategories.map((category) => (
                    <AccordionItem
                      key={category.id}
                      value={category.id}
                      className="border-0"
                    >
                      {/* MAIN LABEL */}
                      <AccordionTrigger className="hover:no-underline py-2 px-3 hover:bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div
                            className={cn("w-2 h-2 rounded-full", category.color ? category.color : 'bg-blue-500')}
                          />
                          <span className="text-sm text-gray-700">
                            {category.label}
                          </span>
                          {searchQuery && (
                            <Badge
                              variant="secondary"
                              className="ml-auto text-xs"
                            >
                              {category.items.length}
                            </Badge>
                          )}
                        </div>
                      </AccordionTrigger>
                      {/* MAIN LABEL END */}
                      <AccordionContent className="pb-2 pt-1">
                        <div className="space-y-1">
                          {/* CHILDREN ITEMS START */}
                          {renderMenuItems(category.items)}
                          {/* CHILDREN ITEMS END */}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : searchQuery ? (
                <div className="pt-8 pb-6 px-3 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                      <Search className="w-6 h-6 text-gray-400" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-900">No results found</p>
                      <p className="text-xs text-gray-500">
                        Try searching with different keywords
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSearchQuery("")}
                      className="mt-2 text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                    >
                      Clear search
                    </Button>
                  </div>
                </div>
              ) : null
            ) : (
              // Collapsed sidebar - show icons only (desktop only)
              <div className="space-y-1 pt-2 lg:block">
                {filteredCategories
                  .flatMap((category) => category.items)
                  .map((item, index) => (
                    <div key={item.path || index} className="relative">
                      {item.children?.length ? (
                        < DropdownMenu
                          open={hoveredItem === item.label}
                          onOpenChange={(open) => {
                            if (!open) setHoveredItem(null);
                          }}
                        >
                          <DropdownMenuTrigger asChild>
                            <button
                              // onMouseEnter={() => setHoveredItem(item.label)}
                              onPointerEnter={() => setHoveredItem(item.label)}
                              className={cn(
                                "w-full flex items-center justify-center px-2 py-2.5 rounded-lg transition-all",
                                "hover:bg-gray-50 text-gray-700"
                              )}
                              title={item.label}
                            >
                              <div className={cn("p-1.5 rounded-lg", item.iconBg)}>
                                {item.icon}
                              </div>
                            </button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent
                            side="right"
                            align="start"
                            className="w-56"
                            // onMouseEnter={() => setHoveredItem(item.label)}
                            // onMouseLeave={() => setHoveredItem(null)}
                            onPointerLeave={() => setHoveredItem(null)}
                          >
                            <DropdownMenuLabel>
                              {item.label}
                            </DropdownMenuLabel>

                            <DropdownMenuSeparator />

                            {item.children?.map((child) => (
                              <DropdownMenuItem
                                key={child.path}
                                onClick={() => {
                                  handleNavigation(child.path);
                                  setHoveredItem(null);
                                }}
                              >
                                {child.icon && (
                                  <span className="mr-2">
                                    {child.icon}
                                  </span>
                                )}

                                {child.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <button
                          onClick={() => item.path && handleNavigation(item.path)}
                          className={cn(
                            "w-full flex items-center justify-center px-2 py-2.5 rounded-lg transition-all",
                            currentPage === item.path
                              ? "bg-sky-50 text-sky-900"
                              : "hover:bg-gray-50 text-gray-700"
                          )}
                          title={item.label}
                        >
                          <div className={cn("p-1.5 rounded-lg", item.iconBg)}>
                            {item.icon}
                          </div>
                        </button>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </aside >

        {/* MAIN CONTENT */}
        < div className="flex-1 flex flex-col min-w-[unset] w-full lg:w-auto overflow-hidden" >
          {/* Header */}
          <header
            className={cn(
              "fixed left-0 right-0 top-0 z-30 bg-white border-b border-gray-200 px-3 py-2 sm:px-2 lg:px-2 transition-all duration-300",
              sidebarCollapsed && !sidebarOpen ? "lg:left-16" : "lg:left-64"
            )}
            style={{paddingTop: "11px",height: "61px",}}
          >
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden shrink-0"
                >
                  <Menu className="w-5 h-5" />
                </Button>
                
                {pageHeader && (
                  <div className="flex min-w-0 items-center gap-1" style={{ alignItems: "center" }}>
                    {pageHeader.onBack && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={pageHeader.onBack}
                        className="h-6 w-6 shrink-0 p-0 mr-1"
                      >
                        <Icons.ArrowLeft className="h-5 w-5" style={{ color: "#5B9BD5" }} />
                      </Button>
                    )}
                    {pageHeader.icon && (
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center text-[#5B9BD5]">
                        {React.isValidElement(pageHeader.icon)
                          ? React.cloneElement(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            pageHeader.icon as React.ReactElement<any>,
                            {
                              className: "w-6 h-6",
                            }
                          )
                        : pageHeader.icon}
                      </span>
                    )}
                    <div className="min-w-0">
                      <h2 className="truncate text-sm font-semibold leading-5 text-[#5B9BD5] sm:text-base">
                        {pageHeader.title}
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        {pageHeader?.childTitle}
                      </p>
                    </div>
                  </div>
                )}

                {/* <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSidebar}
                  className="hidden lg:flex shrink-0"
                >
                  {sidebarCollapsed ? (
                    <PanelLeft className="w-5 h-5" />
                  ) : (
                    <PanelLeftClose className="w-5 h-5" />
                  )}
                </Button> */}

                {/* <div className="relative hidden md:block flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="search"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-full h-9"
                  />
                </div> */}
              </div>

              <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                
                {!!user?.tourOperatorId && user.officeName?.trim() && (
                  <Badge
                    variant="secondary"
                    className="gap-2 rounded-full border-0 bg-red-100 text-red-700 px-3 py-1 max-w-[160px] sm:max-w-[240px] lg:max-w-[360px]"
                    title={`Tour Operator · ${user.officeName}`}
                  >
                    <Icons.BriefcaseBusiness className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span className="truncate">Tour Operator · {user.officeName}</span>
                  </Badge>
                )}

                {isTourOperatorExpired && (
                  <Badge
                    variant="outline"
                    className="border-red-300 bg-red-50 text-red-600 font-medium whitespace-nowrap"
                  >
                    Tour Operator Expired
                  </Badge>
                )}


                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-1 sm:gap-2 h-9 px-2 sm:px-3"
                    >
                      <Avatar className="w-7 h-7">
                        <AvatarFallback className="bg-sky-100 text-sky-700 text-xs">
                          {user?.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("") || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="hidden xl:block text-left">
                        <p className="text-sm">{user?.name}</p>
                        {/* <p className="text-xs text-gray-500">{user?.role}</p> */}
                      </div>
                      <ChevronDown className="w-4 h-4 text-gray-500 hidden sm:block" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div>
                        <p className="text-sm">
                          {user?.name}
                        </p>
                        {/* <p className="text-xs">{user?.officeName && ` (${user.officeName})`}</p> */}
                        <p className="text-xs text-gray-500">{user?.email}</p>
                        {/* <p className="text-xs text-gray-500">{user?.itsNumber}</p> */}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/profile")}>
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    {/* <DropdownMenuItem
                    >
                      <Monitor className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem> */}
                    {/* <DropdownMenuSeparator /> */}
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-600"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header >

          {/* Page Content */}
          {/* <main className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6 w-full">
            <div className="max-w-full">{children}</div>
          </main> */}

          <main className="flex-1 overflow-y-auto p-2 sm:p-2 lg:p-2 w-full" style={{ backgroundColor: "#EEF5FD" }}>
            <div
              className={cn(`flex-1 min-h-0 transition-all duration-300 pt-16`,
                sidebarCollapsed && !sidebarOpen ? "lg:ml-16" : "lg:ml-64")}>
              {children}
            </div>
          </main>
        </div >


        {/* MOBILE SIDEBAR OVERLAY */}
        {
          sidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )
        }

        {/* NOTIFICATIONS PANEL */}
        {
          notificationsOpen && (
            <div className="fixed inset-0 z-50 flex justify-end pointer-events-none">
              <div className="fixed inset-0 bg-black/30 pointer-events-auto" onClick={() => setNotificationsOpen(false)} />
              <div
                ref={notifPanelRef}
                className="relative w-full max-w-sm h-full bg-white border-l border-gray-200 shadow-2xl flex flex-col pointer-events-auto"
                style={{ animation: 'slideInRight 0.22s ease' }}
              >
                {/* Panel Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5" style={{ color: '#5B9BD5' }} />
                    <h2 className="font-semibold text-gray-900 text-base">Notifications</h2>
                    {unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 leading-none">{unreadCount}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-xs text-blue-600 hover:underline font-medium"
                      >
                        Mark all read
                      </button>
                    )}
                    <button
                      onClick={() => setNotificationsOpen(false)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 px-5">
                  {(['all', 'unread'] as const).map(tab => (
                    <button
                      key={tab}
                      onClick={() => setNotifTab(tab)}
                      className={cn(
                        'py-3 mr-6 text-sm font-medium border-b-2 transition-colors',
                        notifTab === tab
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      )}
                    >
                      {tab === 'all' ? 'All' : `Unread${unreadCount > 0 ? ` (${unreadCount})` : ''}`}
                    </button>
                  ))}
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto py-2">
                  {displayedNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-400">
                      <Bell className="w-10 h-10 opacity-30" />
                      <p className="text-sm">No notifications</p>
                    </div>
                  ) : (
                    displayedNotifications.map(notif => (
                      <button
                        key={notif.id}
                        onClick={() => markRead(notif.id)}
                        className={cn(
                          'w-full text-left flex items-start gap-3 px-5 py-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0',
                          !notif.read && 'bg-blue-50/60 hover:bg-blue-50'
                        )}
                      >
                        {notificationIcon(notif.type)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={cn('text-sm font-medium text-gray-900', !notif.read && 'font-semibold')}>{notif.title}</p>
                            <span className="text-xs text-gray-400 shrink-0 mt-0.5">{notif.time}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">{notif.message}</p>
                        </div>
                        {!notif.read && (
                          <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )
        }
      </div >
    </PageHeaderContext.Provider>
  );
}